import { useTranslation } from 'react-i18next';
import {
  KoobTooltip,
  KoobButton,
  KoobInputText,
  PromotionTag,
  sanitizeRichText,
} from '@koob/ui';
import styled, { useTheme } from 'styled-components';
import { Legend, Stack } from '@koob/margaret';
import { DateRange } from 'components';
import HotelDetailCard from 'components/HotelKeeper/HotelDetailCard';
import UI, {
  RoomTag,
  NoteCard,
  TourOperator,
} from 'components/BookingTunnel/UIComponents';
import { CardTitle, SectionCard, Tag, Text } from 'ui';
import { formatCurrency } from 'utils';
import { isEqual, sum } from 'lodash';
import RoomTags from 'components/Booking/RoomTags';
import { formatDate } from '@koob/dates';
import { useState } from 'react';
import ExternalBookingReference from 'components/Booking/ExternalBookingReference';
import { AdminBookingStateEnum } from '@koob/enums';
import { StayFlexibleRequirements } from '@koob/booking';
import { getNationalityLabel } from '@koob/utils';

const ItemCard = styled(Stack).attrs({
  size: 'full',
  padding: 0.5,
  gutterSize: 0.5,
})`
  border: 1px solid ${({ theme }) => theme.separator};
  border-radius: ${({ theme }) => theme.borderRadius.default};
`;

const NoteBloc = styled(Text)`
  max-height: 260px;
  overflow: auto;
`;

const DetailsCard = ({ index, composition, allotment, rooms, currency }) => {
  const { t } = useTranslation(['bookings', 'bookingForm', 'misc']);
  const theme = useTheme();
  const hotelRoom = composition?.hotelRoom;
  const travelers = composition?.travelers ?? [];
  const travelersAmount = travelers?.length ?? 0;
  const room = rooms?.find(
    ({ roomid }) => roomid === composition?.sourceRoomId,
  );
  const bedRooms = composition?.bedCompositions
    ?.map((bedCompo) => `${bedCompo.quantity} ${bedCompo.label}`)
    ?.join(', ');

  const isUpgradedRoom = composition?.promotions?.some(
    (p) => !!p.upgradedFromRoom && !!p.upgradedToRoom,
  );
  const roomName = room?.room_description ?? hotelRoom?.displayName ?? 'N/A';
  return (
    <UI.DetailsSectionCard key={index}>
      <Stack
        direction="row"
        gutterSize={1}
        alignY="start"
        style={{
          marginBottom: theme.spacing(0.5),
          justifyContent: 'space-between',
        }}
      >
        <UI.CardTitle as="h2" style={{ width: 'fit-content' }}>
          {isUpgradedRoom
            ? composition?.promotions[0]?.upgradedToRoom
            : roomName}
        </UI.CardTitle>
        <Stack direction="column" alignX="end">
          <Stack direction="row" gutterSize={1}>
            <Text type="bodyLarge" color="text" fontWeight={700}>
              {formatCurrency({
                amount:
                  composition?.supplierCostInCurrency ??
                  composition?.dmcPriceWithoutMargin,
                currency: currency,
                useCurrencyPrecision: true,
              })}
            </Text>
          </Stack>
        </Stack>
      </Stack>

      {isUpgradedRoom && (
        <Tag variant="primary">
          {`Upgraded room from ${roomName} to ${composition?.promotions[0]?.upgradedToRoom}`}
        </Tag>
      )}
      <Stack direction="row" gutterSize={0.5}>
        <RoomTag
          value={t('nGuests', {
            count: travelersAmount,
          })}
          icon={<i className="fa-light fa-users" style={{ fontSize: 18 }} />}
        />
        {bedRooms && (
          <RoomTag
            value={bedRooms}
            icon={
              <i className="fa-regular fa-hotel" style={{ fontSize: 18 }} />
            }
          />
        )}
      </Stack>
      {allotment && (
        <Stack direction="row" gutterSize={0.5}>
          <RoomTag
            value="Allotment"
            icon={<i className="fa-regular fa-bolt" style={{ fontSize: 18 }} />}
          />
        </Stack>
      )}
      {composition?.promotions?.length > 0 && (
        <Stack
          style={{
            flexWrap: 'wrap',
            gap: '0.5rem',
          }}
        >
          {composition?.promotions?.map((promotion, index) => (
            <>
              {isUpgradedRoom ? (
                <KoobTooltip
                  tip={`Upgraded room from ${roomName} to ${composition?.promotions[0]?.upgradedToRoom}`}
                  position="right"
                  hasArrow={true}
                  color="purple"
                >
                  <PromotionTag key={index} name={promotion.value} />
                </KoobTooltip>
              ) : (
                <PromotionTag key={index} name={promotion.value} />
              )}
            </>
          ))}
        </Stack>
      )}

      {composition?.tags?.length > 0 && (
        <Stack
          style={{
            flexWrap: 'wrap',
            gap: '0.5rem',
          }}
        >
          <RoomTags room={composition} />
        </Stack>
      )}

      <ul style={{ paddingTop: '1rem' }}>
        {travelers?.map((traveler, index) => (
          <UI.DetailSectionItem key={index}>
            <UI.UserIcon kind={traveler?.kind} color={theme.textLighter} />
            <Stack direction="column" gutterSize={0.5} size="full">
              <UI.Text isName>
                {traveler?.firstName} {traveler?.lastName}
              </UI.Text>
              <Stack
                direction="row"
                style={{ flexWrap: 'wrap', gap: '0.5rem' }}
              >
                {traveler?.gender && (
                  <RoomTag
                    value={t(
                      traveler.gender === 'non_binary'
                        ? 'other'
                        : traveler.gender,
                      { ns: 'bookingForm' },
                    )}
                    icon={
                      <i
                        className="fa-light fa-venus-mars"
                        style={{ fontSize: 16 }}
                      />
                    }
                  />
                )}
                {traveler?.birthdate && (
                  <RoomTag
                    value={formatDate(traveler.birthdate, 'DD / MM / YYYY')}
                    icon={
                      <i
                        className="fa-light fa-cake-candles"
                        style={{ fontSize: 16 }}
                      />
                    }
                  />
                )}
                {traveler?.nationality && (
                  <RoomTag
                    value={getNationalityLabel(traveler.nationality)}
                    icon={
                      <i
                        className="fa-light fa-earth-americas"
                        style={{ fontSize: 16 }}
                      />
                    }
                  />
                )}
                {traveler?.passportNumber && (
                  <RoomTag
                    value={
                      traveler?.expirationDate
                        ? `${t('passportNumber', { ns: 'bookingForm' })}: ${
                            traveler.passportNumber
                          } · ${t('expirationDate', {
                            ns: 'bookingForm',
                          })}: ${formatDate(
                            traveler.expirationDate,
                            'DD / MM / YYYY',
                          )}`
                        : `${t('passportNumber', { ns: 'bookingForm' })}: ${
                            traveler.passportNumber
                          }`
                    }
                    icon={
                      <i
                        className="fa-light fa-passport"
                        style={{ fontSize: 16 }}
                      />
                    }
                  />
                )}
              </Stack>
            </Stack>
          </UI.DetailSectionItem>
        ))}
      </ul>
    </UI.DetailsSectionCard>
  );
};

const DateChunk = styled.span`
  text-align: center;
  white-space: nowrap;
`;

const DateRangeHotelKeeper = ({ startAt, endAt, size }) => (
  <Stack gutterSize={0.5} size={size} alignX="space-between">
    {Boolean(startAt) ? (
      <DateChunk>{formatDate(startAt, 'DD MMM YYYY').toUpperCase()}</DateChunk>
    ) : (
      <DateChunk>--/--/----</DateChunk>
    )}
    <i className="fa-light fa-arrow-right p-2" style={{ fontSize: 20 }} />
    {Boolean(endAt) ? (
      <DateChunk>{formatDate(endAt, 'DD MMM YYYY').toUpperCase()}</DateChunk>
    ) : (
      <DateChunk>--/--/----</DateChunk>
    )}
  </Stack>
);

const BookingRecap = ({
  booking,
  onSubmit,
  isSubmitting,
  hotelKeeper = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation('bookings');
  const [hotelConfirmationNumber, setHotelConfirmationNumber] = useState(null);
  const isActiveBoooking =
    booking?.state !== AdminBookingStateEnum.CANCELLED &&
    booking?.state !== AdminBookingStateEnum.REFUSED;
  // The hotel is quoted its contract: when the API hands the net over in the
  // contract currency, it replaces the selling-currency net.
  const supplierCostView = booking?.supplierCostView;
  const netCurrency = supplierCostView?.currency ?? booking?.currency;

  return (
    <Stack direction="column" size="full" alignX="stretch">
      {/* Above the columns: the nights to hold concern the whole booking, and
          the hotel keeper is the one who has to hold them. */}
      <StayFlexibleRequirements
        requirements={booking?.stayFlexibleRequirements}
        currency={booking?.currency}
        translationKey="bookingForm"
      />

      <UI.Wrapper>
        <Stack direction="column" gutterSize={1} style={{ flex: 2 }}>
          <CardTitle>{t('bookingDetails', { ns: 'bookings' })}</CardTitle>

          <SectionCard>
            <Stack direction="column" gutterSize={1} size="full">
              <HotelDetailCard hotel={booking?.hotel} />
              <Stack
                size="full"
                alignX="space-between"
                alignY="center"
                gutterSize={2}
              >
                <ItemCard>
                  {!hotelKeeper ? (
                    <DateRange
                      startAt={booking?.startAt}
                      endAt={booking?.endAt}
                      size="full"
                    />
                  ) : (
                    <DateRangeHotelKeeper
                      startAt={booking?.startAt}
                      endAt={booking?.endAt}
                      size="full"
                    />
                  )}
                </ItemCard>

                {Boolean(booking?.startAt) && Boolean(booking?.endAt) && (
                  <Stack gutterSize={0.25} alignY="center">
                    <i className="fa-light fa-moon" style={{ fontSize: 18 }} />
                    <Legend style={{ whiteSpace: 'nowrap' }}>
                      {t('nNightsStay', {
                        count: booking?.numberOfNights,
                        days: booking?.numberOfNights + 1,
                      })}
                    </Legend>
                  </Stack>
                )}

                <ItemCard>
                  <Stack gutterSize={0.25} alignY="center">
                    <i className="fa-light fa-users" style={{ fontSize: 18 }} />
                    <span>
                      {t('nGuests', {
                        count: sum(
                          (booking?.roomCompositions || []).map(
                            ({ travelers }) => travelers?.length || 0,
                          ),
                        ),
                      })}
                    </span>
                  </Stack>
                  <Stack gutterSize={0.25} alignY="center">
                    <i
                      className="fa-regular fa-sitemap"
                      style={{ fontSize: 18 }}
                    />
                    <span>
                      {t('nRooms', {
                        count: (booking?.roomCompositions || []).length,
                      })}
                    </span>
                  </Stack>
                </ItemCard>
              </Stack>
              <ExternalBookingReference booking={booking} />

              <Stack
                size="full"
                alignY="center"
                gutterSize={1}
                alignX="space-between"
              >
                <Stack alignY="center" gutterSize={1}>
                  <div>
                    <Text fontWeight={700} type="bodyLarge">
                      {t('total')}
                    </Text>
                  </div>
                  {booking?.allotmentAvailable && (
                    <KoobTooltip
                      tip={t('allotments:tooltip')}
                      hasArrow={false}
                      color="purple"
                    >
                      <div className="bg-green-100 rounded-full h-6 w-6 flex items-center justify-center">
                        <i className="fa fa-bolt text-green-500" />
                      </div>
                    </KoobTooltip>
                  )}
                </Stack>
                <Stack gutterSize={1} alignY="center">
                  <Text color="orange" type="bodyLarge" fontWeight={700}>
                    {formatCurrency({
                      amount:
                        supplierCostView?.total ??
                        booking?.totalPriceWithoutMargin,
                      currency: netCurrency,
                      useCurrencyPrecision: true,
                    })}
                  </Text>
                </Stack>
              </Stack>
            </Stack>
          </SectionCard>

          <CardTitle>{t('notes', { ns: 'bookingForm' })}</CardTitle>
          <SectionCard>
            {booking?.note ? (
              <NoteCard>
                <NoteBloc type="body" color="black">
                  <div
                    className="ql-editor"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeRichText(booking?.note),
                    }}
                  />
                </NoteBloc>
              </NoteCard>
            ) : (
              <Text
                as="div"
                type="bodySmall"
                color="gray"
                style={{ textAlign: 'center' }}
              >
                {t('emptyNotes', { ns: 'bookings' })}
              </Text>
            )}
          </SectionCard>

          {/* Rooms details DMC */}
          <CardTitle>{t('roomDetails')}</CardTitle>
          {booking?.roomCompositions?.map((composition, index) => {
            return (
              <DetailsCard
                key={index}
                composition={composition}
                index={index}
                currency={netCurrency}
                allotment={booking?.allotmentAvailable}
                rooms={booking?.rooms}
              />
            );
          })}
        </Stack>

        {/* Right column */}
        {hotelKeeper && (
          <Stack direction="column" style={{ flex: 1 }} gutterSize={1}>
            <div className=" flex flex-col space-y-2 w-full">
              <div className="mt-6">
                <CardTitle>{t('hotelConfirmationNumber')}</CardTitle>
                <KoobInputText
                  style={{ marginTop: '0.5rem', paddingTop: '1rem' }}
                  defaultValue={booking?.hotelConfirmationNumber}
                  onChange={(event) => {
                    setHotelConfirmationNumber(event.target.value);
                  }}
                  disabled={!isActiveBoooking}
                />
              </div>
              {hotelConfirmationNumber &&
                !isEqual(
                  hotelConfirmationNumber,
                  booking?.hotelConfirmationNumber,
                ) && (
                  <div>
                    <KoobButton
                      variant="primary"
                      type="button"
                      onClick={() => {
                        onSubmit({ hotelConfirmationNumber });
                      }}
                      loading={isSubmitting}
                    >
                      {t('misc:save')}
                    </KoobButton>
                  </div>
                )}
            </div>
            {booking?.showToTa && (
              <>
                <CardTitle>
                  {t('bookingReferee', { ns: 'bookingForm' })}
                </CardTitle>
                <SectionCard>
                  <Stack direction={{ default: 'column' }} gutterSize={0.5}>
                    <Stack size="full" direction="column" gutterSize={0.5}>
                      <strong>
                        {booking?.customerFirstname} {booking?.customerLastname}
                      </strong>
                      {booking?.customerCompany && (
                        <Text>{booking?.customerCompany}</Text>
                      )}
                      <Text>{booking?.customerAddress}</Text>
                      {booking?.customerAdditionalAddress && (
                        <Text>{booking?.customerAdditionalAddress}</Text>
                      )}
                      <Text>
                        {booking?.customerZipCode} {booking?.customerCity}{' '}
                      </Text>
                      {Boolean(booking?.customerAdditionalAddress) && (
                        <Text color="textLight">
                          {booking.customerAddressTwo}
                        </Text>
                      )}
                    </Stack>

                    <Stack size="full" direction="column" gutterSize={0.25}>
                      <Stack alignY="center" gutterSize={0.5}>
                        <Stack>
                          <i
                            className="fa-light fa-envelope"
                            style={{ fontSize: 18, color: theme.textLighter }}
                          />
                        </Stack>
                        <Text>{booking?.customerEmail}</Text>
                      </Stack>
                    </Stack>
                    <Stack alignY="center" gutterSize={0.5}>
                      <Stack>
                        <i
                          className="fa-light fa-phone"
                          style={{ fontSize: 18, color: theme.textLighter }}
                        />
                      </Stack>
                      <Text>{booking?.customerPhoneNumber}</Text>
                    </Stack>
                  </Stack>
                </SectionCard>
                {/* Tour operator */}
                <CardTitle>
                  {t('tourOperator', { ns: 'bookingForm' })}
                </CardTitle>
                <SectionCard>
                  <TourOperator
                    operator={{
                      operatorTourName: booking?.operatorTourName,
                      networkName: booking?.networkName,
                    }}
                  >
                    <Text
                      type="bodySmall"
                      color="gray"
                      as="div"
                      style={{ textAlign: 'center' }}
                    >
                      {t('emptyTourOperator', { ns: 'bookings' })}
                    </Text>
                  </TourOperator>
                </SectionCard>
              </>
            )}
          </Stack>
        )}
      </UI.Wrapper>
    </Stack>
  );
};

export default BookingRecap;
